import React, { useMemo } from 'react';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { useLanguage } from '../i18n/LanguageContext';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

function formatShortDate(iso, lang) {
  if (!iso) return '';
  try {
    const d = new Date(`${iso}T12:00:00`);
    const locale = lang === 'sw' ? 'sw-TZ' : undefined;
    return d.toLocaleDateString(locale, { month: 'short', day: 'numeric' });
  } catch {
    return iso;
  }
}

export default function ExpensesTrendChart({ series, factory = false }) {
  const { t, lang } = useLanguage();

  const data = useMemo(() => {
    const labels = (series || []).map((s) => formatShortDate(s.date, lang));
    const bossLabel = factory ? t('exp.cat.boss.short') : t('exp.cat.boss.retail');
    const staffLabel = factory ? t('exp.cat.staff.short') : t('exp.cat.staff.retail');
    const officeLabel = factory ? t('exp.cat.office.label') : t('exp.cat.office.retail');
    return {
      labels,
      datasets: [
        {
          label: bossLabel,
          data: (series || []).map((s) => Number(s.boss) || 0),
          backgroundColor: 'rgba(79, 70, 229, 0.85)',
          stack: 's',
        },
        {
          label: staffLabel,
          data: (series || []).map((s) => Number(s.staff) || 0),
          backgroundColor: 'rgba(14, 116, 144, 0.85)',
          stack: 's',
        },
        {
          label: officeLabel,
          data: (series || []).map((s) => Number(s.office) || 0),
          backgroundColor: 'rgba(5, 122, 85, 0.85)',
          stack: 's',
        },
      ],
    };
  }, [series, factory, t, lang]);

  const options = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { position: 'top' },
        title: { display: false },
        tooltip: {
          callbacks: {
            label(ctx) {
              const v = ctx.raw;
              return `${ctx.dataset.label}: TZS ${Number(v).toLocaleString()}`;
            },
            footer(items) {
              const sum = items.reduce((a, i) => a + (Number(i.raw) || 0), 0);
              return t('exp.trend.dayTotal', { amount: sum.toLocaleString() });
            },
          },
        },
      },
      scales: {
        x: { stacked: true, ticks: { maxRotation: 45, minRotation: 0 } },
        y: {
          stacked: true,
          ticks: {
            callback(v) {
              return Number(v) >= 1000 ? `${(Number(v) / 1000).toFixed(0)}k` : v;
            },
          },
        },
      },
    }),
    [t]
  );

  if (!series || series.length === 0) {
    return null;
  }

  const hasData = series.some((s) => (Number(s.boss) || 0) + (Number(s.staff) || 0) + (Number(s.office) || 0) > 0);
  if (!hasData) {
    return <p className="expenses-chart-empty">{t('exp.chart.empty')}</p>;
  }

  return (
    <div className="expenses-chart-canvas">
      <Bar data={data} options={options} />
    </div>
  );
}
